import { useState } from 'react';
import { decidePermission } from '../api';
import type { PermissionRequest } from '../types';
import { classNames } from '../util';

interface Props {
  request: PermissionRequest;
  onDecided: () => void;
}

export function PermissionCard({ request, onDecided }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFull, setShowFull] = useState(false);

  const isClosed = request.status !== 'open';

  async function decide(d: 'approve' | 'deny') {
    if (submitting || isClosed) return;
    setSubmitting(true);
    setError(null);
    try {
      await decidePermission(request.id, d);
      onDecided();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <article className={classNames('p-card', isClosed && 'is-closed')}>
      <header className="p-head">
        <span className="p-agent">{request.agent_id}</span>
        <span className="pill pill-meta">{request.tool}</span>
      </header>
      <pre className="p-summary">{request.args_summary}</pre>
      {request.args_full && (
        <>
          <button className="q-context-toggle" onClick={() => setShowFull(!showFull)}>
            {showFull ? 'Hide args' : 'Show full args'}
          </button>
          {showFull && (
            <pre className="q-context">{JSON.stringify(request.args_full, null, 2)}</pre>
          )}
        </>
      )}
      {!isClosed && (
        <div className="p-actions">
          <button className="btn btn-danger" onClick={() => decide('deny')} disabled={submitting}>
            Deny
          </button>
          <button className="btn btn-primary" onClick={() => decide('approve')} disabled={submitting}>
            Approve
          </button>
        </div>
      )}
      {error && <div className="q-error">{error}</div>}
    </article>
  );
}
